import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Building2, MapPin, BadgeCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { COMPANY } from "../../data/content";

const EXPERTISE = [
  "Web Development",
  "Mobile Apps",
  "Cyber Security",
  "Amazon FBA",
  "Shopify",
  "eBay & Etsy",
  "AI Integration",
  "Custom Systems",
  "Networks & Servers",
  "Databases",
];

export const About = () => {
  return (
    <section id="about" className="scroll-mt-20 py-16 md:py-24" data-testid="about-section">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <p className="text-sm font-medium text-primary tracking-wide uppercase">About</p>
            <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-3">
              Meet the person behind FCY IT
            </h2>

            <div className="flex items-center gap-4 mt-8">
              <Avatar className="h-16 w-16 border-2 border-primary/30">
                <AvatarFallback className="bg-primary/15 text-primary font-display font-semibold text-xl">
                  FY
                </AvatarFallback>
              </Avatar>
              <div>
                <h3 className="font-display text-xl font-semibold text-foreground" data-testid="about-director-name">
                  {COMPANY.director}
                </h3>
                <p className="text-sm text-muted-foreground">Director &amp; Lead Consultant</p>
              </div>
            </div>

            <p className="text-muted-foreground mt-6 leading-relaxed">
              I studied Computer Instructional &amp; Educational Teaching at Bahcesehir
              University in Istanbul, and turned that foundation into a career building
              digital solutions for businesses of every size. From single-page websites to
              complete warehouse management systems, from Amazon FBA operations to enterprise
              cyber security — if your business needs technology, I make it work.
            </p>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              My approach is simple: you describe your business and what you need, and I
              design, build and launch the system that fits — explained in plain English,
              delivered on time.
            </p>

            <div className="flex flex-wrap gap-2 mt-8">
              {EXPERTISE.map((skill) => (
                <Badge
                  key={skill}
                  variant="outline"
                  className="rounded-full border-white/10 bg-white/5 text-muted-foreground px-3 py-1.5 text-xs"
                >
                  {skill}
                </Badge>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-5 space-y-4"
          >
            <div className="glass-card p-6" data-testid="about-education-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/25 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5 text-primary" />
                </div>
                <h4 className="font-display font-semibold text-foreground">Education</h4>
              </div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Bahcesehir University, Istanbul
                <br />
                <span className="text-foreground">Computer Instructional &amp; Educational Teaching</span>
              </p>
            </div>

            <div className="glass-card p-6" data-testid="about-company-card">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-accent/10 border border-accent/25 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-accent" />
                </div>
                <h4 className="font-display font-semibold text-foreground">Registered UK Company</h4>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">
                    {COMPANY.name}
                    <br />
                    Company No. <span className="text-foreground">{COMPANY.companyNumber}</span>
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground">{COMPANY.address}</span>
                </div>
              </div>
            </div>

            <div className="glass-card p-6" data-testid="about-promise-card">
              <h4 className="font-display font-semibold text-foreground">The FCY IT Promise</h4>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Plain English, no jargon
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> One point of contact, start to finish
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Built, launched and supported
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
